import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AreaChart, ColumnChart } from '@/features/dashboard-charts';
import { getExportOptions } from '@/features/chart-export';
import { DashboardFilterBar } from '@/features/dashboard-filters';
import { PageHeader } from '@/shared/ui';

import { useStorageOverview } from '../lib/use-storage-overview';
import { ChartSection, TopListsGrid, MovementsStats, StockOnHandStats } from './components';

export function StorageOverviewPage() {
  const { t } = useTranslation();
  const {
    data,
    formattedStats,
    locationsData,
    isLoading,
    isFetching,
    locationsLoading,
    error,
    urlState,
    selectedLocations,
    updateOption,
    handleDateRangeChange,
    setSelectedLocations,
    projectId,
    siteId
  } = useStorageOverview();

  const movementsExportOptions = useMemo(() => getExportOptions('warehouse-movements'), []);
  const sohExportOptions = useMemo(() => getExportOptions('warehouse-stock-on-hand'), []);

  const handleTabChange = useCallback(
    (value: string) => {
      updateOption('tab', value);
    },
    [updateOption]
  );

  if (error) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <p className="text-destructive">Error loading storage overview data</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader title={t('report.storageOverview.title', 'Storage Overview')} />

      <Tabs value={urlState.tab} onValueChange={handleTabChange} className="space-y-6">
        <div className="flex w-full items-center justify-between flex-wrap gap-5 flex-col-reverse md:flex-row">
          <TabsList className="grid grid-cols-2 w-full md:w-max">
            <TabsTrigger className="px-12" value="movements">
              {t('welcome.chart.type.smv.label', 'Stock Movements')}
            </TabsTrigger>
            <TabsTrigger className="px-12" value="soh">
              {t('welcome.chart.type.soh.label', 'Stock on Hand')}
            </TabsTrigger>
          </TabsList>

          <DashboardFilterBar
            variant="storage"
            options={urlState}
            onOptionChange={updateOption}
            locationGroups={locationsData?.locationGroups}
            selectedLocations={selectedLocations}
            onLocationsChange={setSelectedLocations}
            locationsLoading={locationsLoading}
            onDateRangeChange={handleDateRangeChange}
            showDateRange={urlState.tab === 'movements'}
            resetKey={`${projectId}-${siteId}`}
          />
        </div>

        <TabsContent value="movements" className="space-y-6">
          <MovementsStats stats={formattedStats} isLoading={isLoading || isFetching} />
          <ChartSection>
            <ColumnChart
              data={data?.warehouseMovements}
              height={400}
              loading={isLoading || isFetching}
              showExport
              exportOptions={movementsExportOptions}
            />
          </ChartSection>
          <TopListsGrid
            topWarehouses={data?.topWarehouses}
            topUsers={data?.topUsers}
            isLoading={isLoading || isFetching}
          />
        </TabsContent>

        <TabsContent value="soh" className="space-y-6">
          <StockOnHandStats stats={formattedStats} isLoading={isLoading || isFetching} />
          <ChartSection>
            <AreaChart
              data={data?.warehouseSOH}
              height={400}
              loading={isLoading || isFetching}
              showExport
              exportOptions={sohExportOptions}
            />
          </ChartSection>
          <TopListsGrid
            topWarehouses={data?.topWarehouses}
            topUsers={data?.topUsers}
            isLoading={isLoading || isFetching}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
